import { Badge, Tooltip } from "@mantine/core";
import { queryTypeMeta } from "./queryTypes";

/**
 * A prompt type as a colour-coded chip, with the explanation on hover.
 *
 * Why the labels are short: Mantine's Badge sets `overflow: hidden; text-overflow: ellipsis` on both
 * the root and the label, and `width: fit-content` — so in a cell narrower than the text it clips
 * silently. "Reputation (Aided) / Aspect" never had a chance, and all four of the long types
 * rendered as the same string, "AWARENESS…", which is worse than no badge: it looked like
 * information. Uppercasing (also a Badge default) costs another ~15% width on top.
 *
 * The fix is to make truncation impossible rather than to fight the clipping — a compact label under
 * ~20 characters, in a column with a fixed width (see PromptsList's Type header). Overriding the
 * overflow instead would spill text across the next column the first time a label did not fit,
 * which is a worse failure than an ellipsis. The full name and the explanation are on the tooltip.
 *
 * `full` renders the long label, for places with room — the create/edit modal, a detail panel.
 */
export default function QueryTypeBadge({ queryType, full = false, ...props }) {
  const meta = queryTypeMeta(queryType);
  if (!meta) return null;

  return (
    <Tooltip label={meta.tooltip} withArrow multiline w={320}>
      <Badge
        size="sm"
        color={meta.color}
        variant={meta.variant}
        // For flex parents — a Group in a Select option, say — where fit-content would otherwise
        // be squeezed by a sibling and clip a label that had room in a table cell.
        style={{ flexShrink: 0 }}
        {...props}
      >
        {full ? meta.label : meta.badgeLabel}
      </Badge>
    </Tooltip>
  );
}
